import { useEffect } from "react";
import OrlandoHeroSection from "./components/HeroSection";
import OrlandoStickyNav from "./components/StickyNav";
import OrlandoProcessingTimeSection from "./components/ProcessingTimeSection";
import OrlandoServicesSection from "./components/ServicesSection";
import OrlandoLocalAreaSection from "./components/LocalAreaSection";
import OrlandoTestimonialsSection from "./components/TestimonialsSection";
import OrlandoAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import OrlandoComparisonSection from "./components/ComparisonSection";
import OrlandoMistakesSection from "./components/MistakesSection";
import OrlandoHowItWorksSection from "./components/HowItWorksSection";
import OrlandoFAQSection, { faqs } from "./components/FAQSection";
import OrlandoHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import OrlandoFinalCTA from "./components/FinalCTA";
import OrlandoTravelTimingSection from "./components/TravelTimingSection";
import OrlandoDestinationsSection from "./components/DestinationsSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function OrlandoPage() {
  useEffect(() => {
    document.title = "Emergency Passport Orlando, FL — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/orlando/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Orlando, FL — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Central Florida travelers.");
    setMeta("geo.placename", "Orlando, Florida");
    setMeta("geo.region", "US-FL");
    setMeta("geo.position", "28.5384;-81.3789");
    setMeta("ICBM", "28.5384, -81.3789");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Florida", item: "https://fastpassportcenter.com/florida/" },
          { "@type": "ListItem", position: 3, name: "Orlando Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center has a physical office at 200 S Orange Ave, Orlando, FL 32801. Emergency and expedited passport services as fast as 24 hours.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        address: {
          "@type": "PostalAddress",
          streetAddress: "200 S Orange Ave",
          addressLocality: "Orlando",
          addressRegion: "FL",
          postalCode: "32801",
          addressCountry: "US",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: 28.5384,
          longitude: -81.3789,
        },
        areaServed: {
          "@type": "City",
          name: "Orlando",
          containedInPlace: {
            "@type": "State",
            name: "Florida",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Orlando",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <OrlandoHeroSection />
      <OrlandoStickyNav />
      <OrlandoProcessingTimeSection />
      <OrlandoServicesSection />
      <OrlandoLocalAreaSection />
      <OrlandoTravelTimingSection />
      <OrlandoDestinationsSection />
      <OrlandoTestimonialsSection />
      <OrlandoAcceptanceFacilitiesSection />
      <OrlandoComparisonSection />
      <OrlandoMistakesSection />
      <OrlandoHowItWorksSection />
      <OrlandoFAQSection />
      <OrlandoHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/orlando" state="FL" />
      <OrlandoFinalCTA />
    </div>
  );
}